import { CanvasAndUser, Role, Subscription } from '@ritabygglov/core';
import { UserData } from '../../../context/User/useUser';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { canvasService } from '../../../apiServices/CanvasService';
import { userService } from '../../../apiServices/UserService';
import { Navbar } from '../../../components/Navbar/Navbar';
import {
  Table,
  TableBody,
  TableHead,
  TableWrapper,
} from '../../../components/Table/Table';
import { useModal } from '../../../context/Modal/useModal';
import { useToast } from '../../../context/Toast/useToast';
import { useDebouncedInput } from '../../../hooks/useDebouncedInput';
import { useMultiQueryState } from '../../../hooks/useQueryState';
import { AdminPagination } from '../AdminPagination';
import { AdminCopyProjectModal } from './AdminCopyProjectModal';
import { GarbageCollectModal } from './GarbageCollectModal';

const getActiveSubscription = (subscriptions: Subscription[]) => {
  return subscriptions.find(
    (sub) => sub.status === 'active' || sub.status === 'trialing'
  );
};

type SortConfig = {
  field: string;
  order: 'asc' | 'desc';
};

const ProjectsTable = ({
  projects,
  sortConfig,
  onSort,
  onGarbageCollect,
  onAdminCopy,
}: {
  projects: CanvasAndUser[];
  sortConfig: SortConfig;
  onSort: (field: string) => void;
  onGarbageCollect: (project: CanvasAndUser) => void;
  onAdminCopy: (project: CanvasAndUser) => void;
}) => {
  const intl = useIntl();
  const getSortIndicator = (field: string) => {
    if (sortConfig.field !== field) return ' ↕';
    return sortConfig.order === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <TableWrapper>
      <Table>
        <colgroup>
          <col style={{ width: '200px' }} />
          <col style={{ width: '220px' }} />
          <col style={{ width: '100px' }} />
          <col style={{ width: '120px' }} />
          <col style={{ width: '140px' }} />
          <col style={{ width: '120px' }} />
        </colgroup>
        <TableHead>
          <tr>
            <th
              onClick={() => onSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.projects.nameHeader' })}
              {getSortIndicator('name')}
            </th>
            <th>{intl.formatMessage({ id: 'admin.projects.emailHeader' })}</th>
            <th>{intl.formatMessage({ id: 'admin.projects.changesCount' })}</th>
            <th
              onClick={() => onSort('createdAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.projects.createdHeader' })}
              {getSortIndicator('createdAt')}
            </th>
            <th
              onClick={() => onSort('updatedAt')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {intl.formatMessage({ id: 'admin.projects.updatedHeader' })}
              {getSortIndicator('updatedAt')}
            </th>
            <th>{intl.formatMessage({ id: 'admin.projects.actions' })}</th>
          </tr>
        </TableHead>
        <TableBody>
          {projects.map((project, index) => (
            <tr key={index}>
              <td title={project.name}>
                <Link
                  to={`/projects/export/${project.canvasId}`}
                  target="_blank"
                >
                  {project.name}
                </Link>
              </td>
              <td title={project.user?.email}>
                {project.user?.subscriptions &&
                  (() => {
                    const activeSub = getActiveSubscription(
                      project.user.subscriptions
                    );
                    return activeSub ? <span>⭐</span> : null;
                  })()}
                <Link to={`/admin/users/${project.user?.userId}/projects`}>
                  {project.user?.email}
                </Link>
              </td>
              <td>{project.changeCount ?? 0}</td>
              <td>
                {new Date(project.createdAt).toLocaleString('sv-SV', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </td>
              <td>
                {new Date(project.updatedAt).toLocaleString('sv-SV', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </td>
              <td>
                {(() => {
                  const hasNoChanges = !project.changeCount;
                  return (
                    <>
                      <button
                        onClick={() => onGarbageCollect(project)}
                        disabled={hasNoChanges}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: hasNoChanges ? 'not-allowed' : 'pointer',
                          opacity: hasNoChanges ? 0.5 : 1,
                          marginRight: '4px',
                        }}
                        title={
                          hasNoChanges
                            ? intl.formatMessage({
                                id: 'admin.projects.noChangesToClear',
                              })
                            : intl.formatMessage({
                                id: 'admin.projects.clearChanges',
                              })
                        }
                      >
                        🗑️ GC
                      </button>
                      <button
                        onClick={() => onAdminCopy(project)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                        title={intl.formatMessage({
                          id: 'admin.copyProject.title',
                        })}
                      >
                        {intl.formatMessage({
                          id: 'admin.copyProject.copyButton',
                        })}
                      </button>
                    </>
                  );
                })()}
              </td>
            </tr>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

export const Projects = () => {
  const [projects, setProjects] = useState<CanvasAndUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState<UserData[]>([]);
  const { addToast } = useToast();
  const { onOpen } = useModal();
  const intl = useIntl();

  const [queryState, setQueryState] = useMultiQueryState({
    page: 1,
    limit: 100,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ownerSubscriptionStatus: '',
    ownerEmail: '',
  });

  const [ownerEmailInput, setOwnerEmailInput] = useDebouncedInput(
    queryState.ownerEmail,
    (ownerEmail) => setQueryState({ ownerEmail, page: 1 })
  );

  const sortConfig: SortConfig = {
    field: queryState.sortBy,
    order: queryState.sortOrder as 'asc' | 'desc',
  };

  const handleSort = (field: string) => {
    setQueryState({
      sortBy: field,
      sortOrder:
        queryState.sortBy === field && queryState.sortOrder === 'asc'
          ? 'desc'
          : 'asc',
    });
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: queryState.page.toString(),
        limit: queryState.limit.toString(),
        sortBy: queryState.sortBy,
        sortOrder: queryState.sortOrder,
      });

      if (queryState.ownerSubscriptionStatus) {
        params.append(
          'ownerSubscriptionStatus',
          queryState.ownerSubscriptionStatus
        );
      }
      if (queryState.ownerEmail) {
        params.append('ownerEmail', queryState.ownerEmail);
      }

      const { projects, totalCount } = await canvasService.getCanvases(
        params.toString()
      );
      setProjects(projects);
      setTotalCount(totalCount);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : intl.formatMessage({ id: 'admin.projects.fetchError' });
      addToast({
        message: errorMessage,
        type: 'error',
      });
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, [queryState, addToast, intl]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const params = new URLSearchParams({
      page: '1',
      limit: '100',
      role: Role.Admin,
    }).toString();
    userService.getUsers(params).then(({ users }) => {
      setAdminUsers(users);
    });
  }, []);

  const handleGarbageCollect = (project: CanvasAndUser) => {
    onOpen(
      <GarbageCollectModal
        canvasId={project.canvasId}
        canvasName={project.name}
        changeCount={project.changeCount ?? 0}
        onSuccess={fetchProjects}
      />
    );
  };

  const handleAdminCopy = (project: CanvasAndUser) => {
    onOpen(
      <AdminCopyProjectModal
        canvasId={project.canvasId}
        canvasName={project.name}
        ownerUserId={project.userId}
        ownerEmail={project.user?.email ?? ''}
        adminUsers={adminUsers}
        onSuccess={fetchProjects}
      />
    );
  };

  return (
    <div className="admin-page">
      <Navbar />
      <h1>{intl.formatMessage({ id: 'admin.projects.title' })}</h1>

      <div style={{ marginBottom: '20px' }}>
        <p>
          {intl.formatMessage(
            { id: 'admin.projects.total' },
            { count: <strong>{totalCount}</strong> }
          )}
        </p>
      </div>

      <div
        style={{
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          maxWidth: '700px',
        }}
      >
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
            }}
          >
            {intl.formatMessage({ id: 'admin.projects.filterByOwnerStatus' })}
          </label>
          <select
            value={queryState.ownerSubscriptionStatus}
            onChange={(e) =>
              setQueryState({
                ownerSubscriptionStatus: e.target.value,
                page: 1,
              })
            }
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trialing</option>
            <option value="canceled">Canceled</option>
            <option value="paused">Paused</option>
          </select>
        </div>
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '4px',
              fontSize: '12px',
              fontWeight: '600',
              textAlign: 'left',
            }}
          >
            {intl.formatMessage({ id: 'admin.projects.filterByOwnerEmail' })}
          </label>
          <input
            type="text"
            value={ownerEmailInput}
            onChange={(e) => setOwnerEmailInput(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'admin.projects.filterEmailPlaceholder',
            })}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      <AdminPagination
        page={queryState.page}
        totalCount={totalCount}
        limit={queryState.limit}
        onPageChange={(page) => setQueryState({ page })}
        disabled={loading}
      />

      {loading && projects.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {intl.formatMessage({ id: 'admin.projects.loading' })}
        </div>
      ) : (
        <>
          <ProjectsTable
            projects={projects}
            sortConfig={sortConfig}
            onSort={handleSort}
            onGarbageCollect={handleGarbageCollect}
            onAdminCopy={handleAdminCopy}
          />
          <AdminPagination
            page={queryState.page}
            totalCount={totalCount}
            limit={queryState.limit}
            onPageChange={(page) => setQueryState({ page })}
            disabled={loading}
          />
        </>
      )}
    </div>
  );
};

export default Projects;
